import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Phone } from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';

/**
 * Modal that pops up for approved users who haven't added a phone number yet.
 * Once a valid phone is provided it can't be dismissed via the X — it's mandatory.
 */
const PhoneCompletionGate = () => {
  const { user, updateUser } = useAuth();
  const [phone, setPhone] = useState('');
  const [saving, setSaving] = useState(false);

  const needsPhone = !!(user && user.is_approved && !user.is_admin && !user.phone);

  if (!needsPhone) return null;

  const handleSave = async () => {
    const digits = phone.replace(/\D/g, '');
    if (digits.length < 7 || digits.length > 15) {
      toast.error('Please enter a valid phone number (7-15 digits)');
      return;
    }
    setSaving(true);
    try {
      await api.put('/users/update', { phone });
      const { data } = await api.get('/auth/me');
      updateUser(data);
      toast.success('Phone number saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save phone number');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={true} modal={true}>
      <DialogContent
        className="sm:max-w-md bg-white"
        // Prevent closing by clicking outside or pressing ESC — phone is mandatory
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
        hideCloseButton
        data-testid="phone-gate-dialog"
      >
        <DialogHeader>
          <div className="flex items-center gap-2 mb-1">
            <Phone className="h-5 w-5 text-[#1E3A8A]" />
            <DialogTitle>Complete your profile</DialogTitle>
          </div>
          <DialogDescription>
            Please add your phone number to continue using GBA Connect. This helps the community
            and admins reach you when needed.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2 py-2">
          <Label htmlFor="phone-input">Phone Number *</Label>
          <Input
            id="phone-input"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+91 98765 43210"
            autoFocus
            data-testid="phone-gate-input"
          />
        </div>
        <Button
          onClick={handleSave}
          disabled={saving || !phone.trim()}
          className="w-full bg-[#1E3A8A] hover:bg-[#172554] text-white"
          data-testid="phone-gate-save-button"
        >
          {saving ? 'Saving...' : 'Save & Continue'}
        </Button>
      </DialogContent>
    </Dialog>
  );
};

export default PhoneCompletionGate;
